import { Flex, Text, Box } from '@chakra-ui/react';
import { Checkbox, FormFeedback, Link } from '@morressier/alembic';
import {
  FormFieldARRIVEFiles,
  FormFieldCONSORTFiles,
  FormFieldFileUploadArray
} from '@publishing-engine/domain/types/article';
import { ISubmissionFieldsResponse } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';
import { useEffect } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { deleteUploadedFile } from 'src/api/queries';
import { FormFieldMultipleFileUpload } from 'src/components/FormField';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import { Guideline } from 'src/pages/Organization/public/Journal/SubmissionUpload/StandardReportingGuidelines';

const DependentCheckbox = ({
  guidelines,
  methodsCheckbox,
  submissionId,
  CONSORTFilesConfig,
  ARRIVEFilesConfig,
  radioValue,
  StandardReportingGuidelinesFileDetailsConfig,
  submissionFields
}: {
  guidelines: Guideline[];
  methodsCheckbox: UseFormReturn<ISubmissionFormData, any, ISubmissionFormData>;
  submissionId: string;
  CONSORTFilesConfig: FormFieldCONSORTFiles | undefined;
  ARRIVEFilesConfig: FormFieldARRIVEFiles | undefined;
  radioValue: boolean;
  StandardReportingGuidelinesFileDetailsConfig?:
    | FormFieldFileUploadArray<'fileSet.StandardReportingGuidelinesFileDetails'>
    | undefined;
  submissionFields: ISubmissionFieldsResponse;
}) => {
  const {
    watch,
    setValue,
    clearErrors,
    unregister,
    formState: { errors }
  } = methodsCheckbox;

  const mainError = errors.preference?.standardReportingGuidelinesDetails;
  const rawValue = watch('preference.standardReportingGuidelinesDetails');
  const CONSORTFile = watch('fileSet.CONSORTFiles') ?? [];
  const ARRIVEFiles = watch('fileSet.ARRIVEFiles') ?? [];
  const StandardReportingGuidelinesDetails =
    watch('fileSet.StandardReportingGuidelinesFileDetails') ?? [];
  const value: string[] = Array.isArray(rawValue)
    ? rawValue
    : rawValue
    ? [rawValue]
    : [];

  const handleGuidelineToggle = (label: string, checked: boolean) => {
    const currentValues = Array.isArray(rawValue)
      ? [...rawValue]
      : rawValue
      ? [rawValue]
      : [];

    const nextValues = checked
      ? Array.from(new Set([...currentValues, label]))
      : currentValues.filter(item => item !== label);

    setValue('preference.standardReportingGuidelinesDetails', nextValues, {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  useEffect(() => {
    if (!value.includes('CONSORT Statement') && CONSORTFile.length > 0) {
      CONSORTFile.forEach(
        item => item.fileId && deleteUploadedFile(item.fileId)
      );
      setValue('fileSet.CONSORTFiles', []);
    }
    if (!value.includes('ARRIVE') && ARRIVEFiles.length > 0) {
      ARRIVEFiles.forEach(
        item => item.fileId && deleteUploadedFile(item.fileId)
      );
      setValue('fileSet.ARRIVEFiles', []);
    }
    if (radioValue === false && StandardReportingGuidelinesDetails.length > 0) {
      StandardReportingGuidelinesDetails.forEach(item => {
        item.fileId && deleteUploadedFile(item.fileId);
      });
      setValue('fileSet.StandardReportingGuidelinesFileDetails', []);
    }
  }, [radioValue, value, setValue, StandardReportingGuidelinesDetails]);

  useEffect(() => {
    if (mainError) clearErrors('preference.standardReportingGuidelinesDetails');
  }, [value.length, radioValue]);

  useEffect(() => {
    if (!radioValue) {
      unregister('preference.standardReportingGuidelinesDetails');
      unregister('fileSet.CONSORTFiles');
      unregister('fileSet.ARRIVEFiles');
      unregister('fileSet.StandardReportingGuidelinesFileDetails');
    } else {
      unregister('preference.standardReportingGuidelinesText');
    }
  }, [radioValue, unregister]);

  return (
    <>
      <FormProvider {...methodsCheckbox}>
        {guidelines.map(item => (
          <Flex key={item.label} direction="column">
            <Box key={item.label}>
              <Checkbox
                isChecked={value.includes(item.label)}
                value={item.label}
                name="preference.standardReportingGuidelinesDetails"
                ariaLabel={item.label}
                id={item.label}
                disabled={!radioValue}
                m={2}
                onChange={e =>
                  handleGuidelineToggle(item.label, e.currentTarget.checked)
                }
              >
                <Link href={item.href} isExternal variant="primary">
                  {item.label}
                </Link>
                <Text as="span" ml={2}>
                  {item.note}
                </Text>
              </Checkbox>
            </Box>
            {value.includes('CONSORT Statement') &&
              item.label === 'CONSORT Statement' && (
                <Flex
                  flexDirection="column"
                  bgColor="surfaceSecondaryFill"
                  m={2}
                  p={4}
                  cursor="default"
                >
                  {CONSORTFilesConfig && (
                    <FormFieldMultipleFileUpload
                      submissionId={submissionId}
                      config={CONSORTFilesConfig}
                      submissionFields={submissionFields}
                    />
                  )}
                </Flex>
              )}
            {value.includes('ARRIVE') && item.label === 'ARRIVE' && (
              <Flex
                flexDirection="column"
                bgColor="surfaceSecondaryFill"
                m={2}
                p={4}
                cursor="default"
              >
                {ARRIVEFilesConfig && (
                  <FormFieldMultipleFileUpload
                    submissionId={submissionId}
                    config={ARRIVEFilesConfig}
                    submissionFields={submissionFields}
                  />
                )}
              </Flex>
            )}
          </Flex>
        ))}
        {StandardReportingGuidelinesFileDetailsConfig && radioValue && (
          <Flex flexDirection="column" m={2} p={4} cursor="default">
            <FormFieldMultipleFileUpload
              submissionId={submissionId}
              config={StandardReportingGuidelinesFileDetailsConfig}
              submissionFields={submissionFields}
            />
          </Flex>
        )}
      </FormProvider>
      {radioValue && mainError && mainError?.message && (
        <FormFeedback variant="error">{mainError?.message}</FormFeedback>
      )}
    </>
  );
};
export default DependentCheckbox;
